import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Card, Button, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import api from '../api/axios';

const ExamReport = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  // Get username from Redux auth slice
  const user = useSelector((state) => state.auth.user);
  const username = user?.username || 'Guest';

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/exam-report.php?sessionId=${sessionId}`);
        setReport(res.data);
      } catch (err) {
        console.error('Failed to load report', err);
        if (err.response) {
          setError(`Server error: ${err.response.status} - ${err.response.data?.error || 'Unknown error'}`);
        } else if (err.request) {
          setError('No response from server. Check your connection.');
        } else {
          setError('Error: ' + err.message);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [sessionId]);

  const getPerformanceLevel = (percentage) => {
    const p = parseFloat(percentage);
    if (p >= 85) return { label: 'Above Passing', variant: 'success' };
    if (p >= 70) return { label: 'Pass', variant: 'primary' };
    if (p >= 60) return { label: 'Near Passing', variant: 'warning' };
    return { label: 'Below Passing', variant: 'danger' };
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading report...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5 text-center">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!report) {
    return (
      <Container className="mt-5 text-center">
        <Alert variant="warning">No report data found.</Alert>
        <Button variant="primary" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  // If report is not available, show appropriate message
  if (!report.reportAvailable) {
    let message = '';
    const now = new Date();
    const releaseTime = report.releaseTime ? new Date(report.releaseTime) : null;

    if (releaseTime && now < releaseTime) {
      message = `The report will be available after ${releaseTime.toLocaleString()}.`;
    } else if (report.completedUsers < report.minimumTakers) {
      message = `The report will be available once at least ${report.minimumTakers} different participants have completed this exam. Currently ${report.completedUsers} have completed.`;
    } else {
      message = 'The report is not available yet.';
    }

    return (
      <Container className="mt-5 text-center" style={{ maxWidth: '600px' }}>
        <h2>Exam Report</h2>
        <Alert variant="info">{message}</Alert>
        <Button variant="primary" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const { session, topicBreakdown, typeBreakdown, percentile } = report;

  return (
    <Container className="mt-4" style={{ maxWidth: '1000px' }}>
      {/* Personalized header */}
      <h2 className="mb-3">Exam Report for {username}</h2>
      <p className="text-muted">Hello, {username}! Here’s your performance summary.</p>

      {/* Overall Performance Card */}
      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Overall Performance</Card.Title>
          <Table bordered className="mt-3">
            <tbody>
              <tr>
                <td><strong>Total Questions</strong></td>
                <td>{session.total_questions}</td>
              </tr>
              <tr>
                <td><strong>Questions Answered</strong></td>
                <td>{report.results.length}</td>
              </tr>
              <tr>
                <td><strong>Total Score</strong></td>
                <td>
                  {session.score} / {session.max_score} ({session.percentage}%)
                </td>
              </tr>
              <tr>
                <td><strong>Practice Time</strong></td>
                <td>{session.practiceTime}</td>
              </tr>
              {percentile !== null && (
                <tr>
                  <td><strong>Percentile Rank</strong></td>
                  <td>{percentile}%</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Percentile Meter (if percentile exists) */}
      {percentile !== null && (
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Percentile Rank</Card.Title>
            <p>You scored better than <strong>{percentile}%</strong> of test‑takers.</p>
            <div style={{ position: 'relative', height: '30px', background: '#e9ecef', borderRadius: '5px' }}>
              <div
                style={{
                  position: 'absolute',
                  left: `${percentile}%`,
                  width: '2px',
                  height: '100%',
                  background: '#000',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${percentile}%`,
                  background: '#0d6efd',
                  borderRadius: '5px 0 0 5px',
                }}
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Topic Breakdown */}
      {topicBreakdown && topicBreakdown.length > 0 && (
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Performance by Topic</Card.Title>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Topic</th>
                  <th>Points</th>
                  <th>Individual Score</th>
                  <th>Performance Level</th>
                </tr>
              </thead>
              <tbody>
                {topicBreakdown.map((topic, idx) => {
                  const level = getPerformanceLevel(topic.percentage);
                  return (
                    <tr key={idx}>
                      <td>{topic.topic}</td>
                      <td>{topic.points}</td>
                      <td>{topic.percentage}%</td>
                      <td>
                        <Badge bg={level.variant}>{level.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Question‑Type Stacked Bars */}
      {typeBreakdown && typeBreakdown.length > 0 && (
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Performance by Question Type</Card.Title>
            <div style={{ marginTop: '10px' }}>
              {typeBreakdown.map((type, idx) => (
                <div key={idx} style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ width: '80px', fontWeight: 'bold' }}>{type.mode}</span>
                    <span style={{ marginLeft: '10px' }}>
                      {type.correct}/{type.total} ({type.percentage}%)
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      height: '24px',
                      width: '100%',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '4px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${type.percentage}%`,
                        backgroundColor: '#28a745',
                        height: '100%',
                      }}
                    />
                    <div
                      style={{
                        width: `${100 - type.percentage}%`,
                        backgroundColor: '#dc3545',
                        height: '100%',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Question Details */}
      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Question Details</Card.Title>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Your Answer</th>
                <th>Correct Answer</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {report.results.map((r, idx) => (
                <tr
                  key={idx}
                  onClick={() => navigate('/exam/review', { state: { startIndex: idx, results: report.results } })}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{idx + 1}</td>
                  <td>{r.selected || '(none)'}</td>
                  <td>{r.correct_answer}</td>
                  {/* Use normalized_score if available, otherwise fallback to original score */}
                  <td>{r.normalized_score !== undefined ? r.normalized_score.toFixed(2) : r.score}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <div className="mt-3 text-center">
        <Button variant="primary" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </div>
    </Container>
  );
};

export default ExamReport;